import { createFileRoute } from '@tanstack/react-router';
import {
    ExternalLink,
    FileSignature,
    Loader2,
    Plus,
    RefreshCw,
    Send,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';

import { FranchiseFormModal } from '@apps/web/components/admin/franchise_form_modal';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import { MultiSelect } from '@apps/web/components/ui/multi_select';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useDebounce } from '@apps/web/hooks/use_debounce';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    useAdminSalesAgreements,
    useAdminSalesPandaDocCatalog,
    useAdminSalesPandaDocTemplates,
    useInternalFranchises,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AgreementCreate,
    type AgreementRead,
    type AgreementStatus,
    type PandaDocCatalogItem,
    type PandaDocTemplateRead,
} from '@libraries/typescript/api_client';

import {
    catalogControlsDisabled,
    effectiveMonthlyPrice,
} from './agreement_catalog';

export const Route = createFileRoute('/_app/admin/sales/agreements')({
    head: () => ({
        meta: [
            {
                title: 'Sales Agreements | Phoebe Admin',
            },
        ],
    }),
    component: AdminSalesAgreementsPage,
});

const STATUS_LABELS: Record<AgreementStatus, string> = {
    draft: 'Draft',
    creating: 'Creating',
    ready: 'Ready',
    sent: 'Sent',
    viewed: 'Viewed',
    recipient_completed: 'Recipient complete',
    completed: 'Completed',
    declined: 'Declined',
    expired: 'Expired',
    deleted: 'Deleted',
    failed: 'Failed',
};

const DEFAULT_TOKENS = `[
  { "name": "Client.Company", "value": "" },
  { "name": "Client.EHR", "value": "" },
  { "name": "Client.FirstName", "value": "" },
  { "name": "Client.LastName", "value": "" },
  { "name": "Phoebe.ClientHours", "value": "" },
  { "name": "Phoebe.TierName", "value": "" },
  { "name": "Phoebe.MonthlyPrice", "value": "" }
]`;

const DEFAULT_FIELDS = `{
  "Entity Legal Name": { "value": "" },
  "Signer Title": { "value": "" },
  "Kick-Off Date": { "value": "" }
}`;

const DEFAULT_ONBOARDING_JSON = `{
  "company_name": "",
  "legal_entity_name": "",
  "ein": "",
  "business_address": "",
  "entity_type": "",
  "support_email": "",
  "preferred_area_code": "",
  "transfer_number": ""
}`;

const DEFAULT_TERM_SKUS = new Set([
    'TERM-DISC-21DAYSFREE',
    'TERM-DISC-WAIVEDFEES',
]);

function parseJson<T>(value: string, fallback: T, label: string): T {
    const trimmed = value.trim();
    if (!trimmed) {
        return fallback;
    }
    try {
        return JSON.parse(trimmed) as T;
    } catch {
        throw new Error(`${label} must be valid JSON`);
    }
}

function formatDateTime(value: string | null | undefined): string {
    if (!value) {
        return '—';
    }
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(new Date(value));
}

function statusVariant(status: AgreementStatus) {
    if (status === 'completed') {
        return 'default' as const;
    }
    if (status === 'failed' || status === 'declined' || status === 'expired') {
        return 'destructive' as const;
    }
    return 'outline' as const;
}

function splitName(name: string): { firstName: string; lastName: string } {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return { firstName: '', lastName: '' };
    }
    const firstName = parts[0] ?? '';
    if (parts.length === 1) {
        return { firstName, lastName: '' };
    }
    return {
        firstName,
        lastName: parts.slice(1).join(' '),
    };
}

function applyDefaultTokenValues(
    tokens: Array<Record<string, unknown>>,
    {
        customerName,
        signerName,
        ehr,
        clientHours,
        tierName,
        monthlyPrice,
    }: {
        readonly customerName: string;
        readonly signerName: string;
        readonly ehr: string;
        readonly clientHours: string;
        readonly tierName: string;
        readonly monthlyPrice: string;
    },
): Array<Record<string, unknown>> {
    const { firstName, lastName } = splitName(signerName);
    const defaults: Record<string, string> = {
        'Client.Company': customerName,
        'Client.EHR': ehr,
        'Client.FirstName': firstName,
        'Client.LastName': lastName,
        'Phoebe.ClientHours': clientHours,
        'Phoebe.TierName': tierName,
        'Phoebe.MonthlyPrice': monthlyPrice,
    };

    return tokens.map((token) => {
        const name = token['name'];
        const value = token['value'];
        if (
            typeof name !== 'string' ||
            value !== '' ||
            defaults[name] === undefined
        ) {
            return token;
        }
        return { ...token, value: defaults[name] };
    });
}

function AdminSalesAgreementsPage() {
    const { user } = Route.useRouteContext();
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<AgreementStatus | 'all'>(
        'all',
    );
    const {
        data: agreementsResponse,
        isLoading,
        mutate,
    } = useAdminSalesAgreements({
        page: 1,
        per_page: 100,
        ...(searchQuery ? { search: searchQuery } : {}),
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    });

    const agreements = agreementsResponse?.agreements ?? [];

    return (
        <div className="flex h-full min-w-0 flex-col overflow-hidden">
            <div className="border-border min-w-0 border-b px-4 py-4 sm:px-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold">Sales</h2>
                        <p className="text-muted-foreground text-sm">
                            Create and track customer agreements from PandaDoc.
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        className="w-full gap-2 sm:w-auto"
                        onClick={() => void mutate()}
                    >
                        <RefreshCw className="h-4 w-4" />
                        Refresh
                    </Button>
                </div>
            </div>
            <div className="min-w-0 flex-1 overflow-y-auto">
                <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 p-4 sm:p-6">
                    <Tabs value="agreements">
                        <TabsList>
                            <TabsTrigger value="agreements">
                                Agreements
                            </TabsTrigger>
                        </TabsList>
                        <TabsContent value="agreements" className="mt-5">
                            <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(360px,420px)_1fr]">
                                <CreateAgreementPanel
                                    canSendImmediately={user?.admin === true}
                                    onCreated={() => void mutate()}
                                />
                                <AgreementsTable
                                    agreements={agreements}
                                    isLoading={isLoading}
                                    searchQuery={searchQuery}
                                    setSearchQuery={setSearchQuery}
                                    statusFilter={statusFilter}
                                    setStatusFilter={setStatusFilter}
                                />
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>
        </div>
    );
}

function CreateAgreementPanel({
    canSendImmediately,
    onCreated,
}: {
    readonly canSendImmediately: boolean;
    readonly onCreated: () => void;
}) {
    const apiClient = getApiClient();
    const { data: templatesResponse, isLoading: templatesLoading } =
        useAdminSalesPandaDocTemplates();
    const {
        data: catalogResponse,
        error: catalogError,
        isLoading: catalogLoading,
    } = useAdminSalesPandaDocCatalog();

    const templates = templatesResponse?.templates ?? [];
    const [customerName, setCustomerName] = useState('');
    const [templateId, setTemplateId] = useState('');
    const [title, setTitle] = useState('');
    const [signerName, setSignerName] = useState('');
    const [signerEmail, setSignerEmail] = useState('');
    const [signerRole, setSignerRole] = useState('Client');
    const [ehr, setEhr] = useState('');
    const [clientHours, setClientHours] = useState('');
    const [pricingCatalogItemIds, setPricingCatalogItemIds] = useState<
        Array<string>
    >([]);
    const [termCatalogItemIds, setTermCatalogItemIds] = useState<Array<string>>(
        [],
    );
    const [discountPercentage, setDiscountPercentage] = useState('');
    const initializedDefaultTerms = useRef(false);
    const [attribution, setAttribution] = useState('');
    const [sendDocument, setSendDocument] = useState(false);
    const [tokensJson, setTokensJson] = useState(DEFAULT_TOKENS);
    const [fieldsJson, setFieldsJson] = useState(DEFAULT_FIELDS);
    const [metadataJson, setMetadataJson] = useState('{}');
    const [onboardingJson, setOnboardingJson] = useState(
        DEFAULT_ONBOARDING_JSON,
    );
    const [franchiseId, setFranchiseId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { data: franchisesResponse, mutate: mutateFranchises } =
        useInternalFranchises();
    const franchiseOptions = franchisesResponse?.franchises ?? [];
    const [isFranchiseModalOpen, setIsFranchiseModalOpen] = useState(false);

    const selectedTemplate = templates.find(
        (template) => template.id === templateId,
    );
    const catalogItems = catalogResponse?.items ?? [];
    const pricingItems = catalogItems.filter((item) => item.price > 0);
    const termItems = catalogItems.filter((item) => item.price === 0);
    const selectedPricingItems = pricingCatalogItemIds
        .map((id) => pricingItems.find((item) => item.id === id))
        .filter((item): item is PandaDocCatalogItem => item !== undefined);
    const primaryPricingItem = selectedPricingItems[0];
    const tierName = primaryPricingItem?.name ?? '';
    const monthlyPrice = effectiveMonthlyPrice(
        primaryPricingItem?.price,
        discountPercentage,
    );
    const areCatalogControlsDisabled = catalogControlsDisabled(
        catalogLoading,
        !!catalogError,
    );
    const defaultTermIds = termItems
        .filter((item) => item.sku && DEFAULT_TERM_SKUS.has(item.sku))
        .map((item) => item.id);

    useEffect(() => {
        if (!initializedDefaultTerms.current && defaultTermIds.length > 0) {
            initializedDefaultTerms.current = true;
            setTermCatalogItemIds(defaultTermIds);
        }
    }, [defaultTermIds]);

    const { data: templateDetails } = useSWR(
        templateId ? ['pandadoc-template-details', templateId] : null,
        async ([, resolvedTemplateId]: [string, string]) =>
            apiClient.get({
                url: '/admin/sales/pandadoc/templates/{template_id}',
                pathParams: { template_id: resolvedTemplateId },
                reason: 'admin-sales-template-details',
            }),
    );

    // Submitting without an organization_id attaches the proposal to an
    // existing organization whose name matches, which is otherwise invisible
    // here. Preview that decision with the same matcher the write path uses.
    const debouncedCustomerName = useDebounce(customerName.trim(), 300);
    const { data: organizationMatch } = useSWR(
        debouncedCustomerName
            ? [
                  'admin-sales-organization-match',
                  debouncedCustomerName,
                  franchiseId,
              ]
            : null,
        async ([, resolvedCustomerName, resolvedFranchiseId]: [
            string,
            string,
            string | null,
        ]) =>
            apiClient.get({
                url: '/admin/sales/organization-match',
                queryParams: {
                    customer_name: resolvedCustomerName,
                    ...(resolvedFranchiseId
                        ? { franchise_id: resolvedFranchiseId }
                        : {}),
                },
                reason: 'admin-sales-organization-match',
            }),
    );
    const matchedOrganizationName =
        organizationMatch?.organization_name ?? null;

    const roleOptions = useMemo(() => {
        const roles = templateDetails?.roles ?? [];
        const roleNames = roles
            .map((role) => role['name'])
            .filter((name): name is string => typeof name === 'string');
        return Array.from(new Set(['Client', ...roleNames]));
    }, [templateDetails]);

    async function handleCreateAgreement() {
        if (
            !customerName ||
            !templateId ||
            !title ||
            !signerName ||
            !signerEmail
        ) {
            toast.error('Enter customer, template, title, and signer details', {
                persistNotification: false,
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const customerEntity = parseJson<Record<string, unknown>>(
                onboardingJson,
                {},
                'Onboarding data',
            );
            const tokens = applyDefaultTokenValues(
                parseJson<Array<Record<string, unknown>>>(
                    tokensJson,
                    [],
                    'Tokens',
                ),
                {
                    customerName,
                    signerName,
                    ehr,
                    clientHours,
                    tierName,
                    monthlyPrice,
                },
            );
            const onboardingData = {
                ...customerEntity,
                company_name:
                    typeof customerEntity['company_name'] === 'string' &&
                    customerEntity['company_name']
                        ? customerEntity['company_name']
                        : customerName,
                ehr_platform:
                    typeof customerEntity['ehr_platform'] === 'string' &&
                    customerEntity['ehr_platform']
                        ? customerEntity['ehr_platform']
                        : ehr,
                weekly_billable_hours:
                    typeof customerEntity['weekly_billable_hours'] ===
                        'string' && customerEntity['weekly_billable_hours']
                        ? customerEntity['weekly_billable_hours']
                        : clientHours,
                pricing_tier_name:
                    typeof customerEntity['pricing_tier_name'] === 'string' &&
                    customerEntity['pricing_tier_name']
                        ? customerEntity['pricing_tier_name']
                        : tierName,
                monthly_price:
                    typeof customerEntity['monthly_price'] === 'string' &&
                    customerEntity['monthly_price']
                        ? customerEntity['monthly_price']
                        : monthlyPrice,
            };
            const body = {
                customer_name: customerName,
                title,
                pandadoc_template_id: templateId,
                pandadoc_template_name: selectedTemplate?.name ?? null,
                signer_name: signerName,
                signer_email: signerEmail,
                signer_role: signerRole,
                send_document: sendDocument,
                tokens_json: tokens,
                fields_json: parseJson<Record<string, unknown>>(
                    fieldsJson,
                    {},
                    'Fields',
                ),
                metadata_json: parseJson<Record<string, unknown>>(
                    metadataJson,
                    {},
                    'Metadata',
                ),
                customer_entity_json: onboardingData,
                onboarding_data_json: onboardingData,
                attribution: attribution || null,
                franchise_id: franchiseId,
                pricing_catalog_item_ids: pricingCatalogItemIds,
                term_catalog_item_ids: termCatalogItemIds,
                discount_percentage: discountPercentage
                    ? Number(discountPercentage)
                    : null,
            } satisfies AgreementCreate;

            await apiClient.post({
                url: '/admin/sales/agreements',
                body,
                reason: 'admin-sales-create-agreement',
            });
            toast.success(
                sendDocument ? 'Agreement sent' : 'Agreement draft created',
                {
                    persistNotification: true,
                    notificationType: 'settings_update',
                },
            );
            setCustomerName('');
            setTitle('');
            setSignerName('');
            setSignerEmail('');
            setEhr('');
            setClientHours('');
            setPricingCatalogItemIds([]);
            setTermCatalogItemIds(defaultTermIds);
            setDiscountPercentage('');
            setAttribution('');
            setFranchiseId(null);
            onCreated();
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : 'Failed to create agreement',
                {
                    persistNotification: true,
                    notificationType: 'form_failure',
                },
            );
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <Card className="min-w-0">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <FileSignature className="h-4 w-4" />
                    Create Agreement
                </CardTitle>
                <CardDescription>
                    Creates the customer org, drafts a PandaDoc document, and
                    stores the onboarding record.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="grid gap-2">
                    <Label>Customer Name</Label>
                    <Input
                        value={customerName}
                        placeholder="SYNERGY HomeCare of Montgomery and Bucks County"
                        onChange={(event) => {
                            const value = event.target.value;
                            setCustomerName(value);
                            if (!title || title.startsWith('Phoebe + ')) {
                                setTitle(
                                    value ? `Phoebe + ${value} Agreement` : '',
                                );
                            }
                        }}
                    />
                </div>
                <div className="grid gap-2">
                    <Label>PandaDoc Template</Label>
                    <Select value={templateId} onValueChange={setTemplateId}>
                        <SelectTrigger>
                            <SelectValue
                                placeholder={
                                    templatesLoading
                                        ? 'Loading templates…'
                                        : 'Choose template'
                                }
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {templates.map((template: PandaDocTemplateRead) => (
                                <SelectItem
                                    key={template.id}
                                    value={template.id}
                                >
                                    {template.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="grid gap-2">
                    <Label>Agreement Title</Label>
                    <Input
                        value={title}
                        placeholder={
                            customerName
                                ? `Phoebe + ${customerName} Agreement`
                                : 'Phoebe + Customer Agreement'
                        }
                        onChange={(event) => setTitle(event.target.value)}
                    />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                    <div className="grid gap-2">
                        <Label>Signer Name</Label>
                        <Input
                            value={signerName}
                            onChange={(event) =>
                                setSignerName(event.target.value)
                            }
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label>Signer Email</Label>
                        <Input
                            value={signerEmail}
                            type="email"
                            onChange={(event) =>
                                setSignerEmail(event.target.value)
                            }
                        />
                    </div>
                </div>
                {matchedOrganizationName !== null ? (
                    <p className="text-muted-foreground text-sm">
                        Matches existing organization{' '}
                        <span className="text-foreground font-medium">
                            “{matchedOrganizationName}”
                        </span>
                        . This proposal will attach to it instead of creating a
                        new one.
                    </p>
                ) : null}
                <div className="grid gap-2">
                    <Label>Signer Role</Label>
                    <Select value={signerRole} onValueChange={setSignerRole}>
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {roleOptions.map((role) => (
                                <SelectItem key={role} value={role}>
                                    {role}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="grid gap-2">
                    <Label>Franchise</Label>
                    <Select
                        value={franchiseId ?? 'none'}
                        onValueChange={(value) => {
                            if (value === '__create__') {
                                setIsFranchiseModalOpen(true);
                                return;
                            }
                            setFranchiseId(value === 'none' ? null : value);
                        }}
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {franchiseOptions.map((franchise) => (
                                <SelectItem
                                    key={franchise.id}
                                    value={franchise.id}
                                >
                                    {franchise.name}
                                </SelectItem>
                            ))}
                            <SelectItem
                                value="__create__"
                                className="text-primary font-medium"
                            >
                                <span className="flex items-center gap-1">
                                    <Plus className="h-3.5 w-3.5" />
                                    Create Franchise
                                </span>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    <FranchiseFormModal
                        isOpen={isFranchiseModalOpen}
                        onClose={() => setIsFranchiseModalOpen(false)}
                        mode="create"
                        onCreated={(franchise) => {
                            setFranchiseId(franchise.id);
                            void mutateFranchises();
                        }}
                    />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                    <div className="grid gap-2">
                        <Label>EHR</Label>
                        <Input
                            value={ehr}
                            placeholder="WellSky, AxisCare, HHAeXchange…"
                            onChange={(event) => setEhr(event.target.value)}
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label>Weekly Billable Hours</Label>
                        <Input
                            value={clientHours}
                            inputMode="numeric"
                            placeholder="950"
                            onChange={(event) =>
                                setClientHours(event.target.value)
                            }
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label>Attribution</Label>
                        <Input
                            value={attribution}
                            placeholder="Gabrielle Referral"
                            maxLength={80}
                            onChange={(event) =>
                                setAttribution(event.target.value)
                            }
                        />
                    </div>
                </div>
                <div className="grid gap-2">
                    <Label>Pricing Tiers</Label>
                    <MultiSelect
                        options={pricingItems.map((item) => ({
                            value: item.id,
                            label: `${item.name} — ${new Intl.NumberFormat(
                                undefined,
                                {
                                    style: 'currency',
                                    currency: item.currency ?? 'USD',
                                    maximumFractionDigits: 0,
                                },
                            ).format(item.price)}/month`,
                        }))}
                        selected={pricingCatalogItemIds}
                        onChange={setPricingCatalogItemIds}
                        placeholder={
                            catalogLoading
                                ? 'Loading PandaDoc catalog…'
                                : 'Select pricing tiers'
                        }
                        disabled={areCatalogControlsDisabled}
                        isLoading={catalogLoading}
                        matchTriggerWidth={true}
                    />
                    <p className="text-muted-foreground text-xs">
                        The first selected tier is used as the current pricing
                        tier in the agreement text.
                    </p>
                    {catalogError ? (
                        <p className="text-destructive text-xs">
                            PandaDoc catalog could not be loaded. You can still
                            create an agreement without catalog selections.
                        </p>
                    ) : null}
                </div>
                <div className="grid gap-2">
                    <Label>Agreement Terms &amp; Discounts</Label>
                    <MultiSelect
                        options={termItems.map((item) => ({
                            value: item.id,
                            label: item.name,
                        }))}
                        selected={termCatalogItemIds}
                        onChange={setTermCatalogItemIds}
                        placeholder={
                            catalogLoading
                                ? 'Loading PandaDoc catalog…'
                                : 'Select terms and discounts'
                        }
                        disabled={areCatalogControlsDisabled}
                        isLoading={catalogLoading}
                        matchTriggerWidth={true}
                    />
                </div>
                <div className="grid gap-2">
                    <Label>Pricing Discount Percentage</Label>
                    <Input
                        value={discountPercentage}
                        type="number"
                        inputMode="decimal"
                        min={0}
                        max={100}
                        step="0.01"
                        placeholder="20"
                        onChange={(event) =>
                            setDiscountPercentage(event.target.value)
                        }
                    />
                    <p className="text-muted-foreground text-xs">
                        Applied to every selected pricing tier. Leave blank for
                        no percentage discount.
                    </p>
                </div>
                <JsonTextarea
                    label="Tokens"
                    value={tokensJson}
                    onChange={setTokensJson}
                    rows={6}
                />
                <JsonTextarea
                    label="Fields"
                    value={fieldsJson}
                    onChange={setFieldsJson}
                    rows={5}
                />
                <JsonTextarea
                    label="Onboarding Data"
                    value={onboardingJson}
                    onChange={setOnboardingJson}
                    rows={7}
                />
                <JsonTextarea
                    label="Metadata"
                    value={metadataJson}
                    onChange={setMetadataJson}
                    rows={3}
                />
                {canSendImmediately ? (
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="send-document"
                            checked={sendDocument}
                            onCheckedChange={(checked) =>
                                setSendDocument(checked === true)
                            }
                        />
                        <Label htmlFor="send-document" className="text-sm">
                            Send immediately
                        </Label>
                    </div>
                ) : null}
                <Button
                    className="w-full gap-2"
                    disabled={isSubmitting}
                    onClick={() => void handleCreateAgreement()}
                >
                    {isSubmitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : sendDocument ? (
                        <Send className="h-4 w-4" />
                    ) : (
                        <FileSignature className="h-4 w-4" />
                    )}
                    {sendDocument ? 'Create and Send' : 'Create Draft'}
                </Button>
            </CardContent>
        </Card>
    );
}

function JsonTextarea({
    label,
    value,
    onChange,
    rows,
}: {
    readonly label: string;
    readonly value: string;
    readonly onChange: (value: string) => void;
    readonly rows: number;
}) {
    return (
        <div className="grid gap-2">
            <Label>{label}</Label>
            <Textarea
                value={value}
                rows={rows}
                spellCheck={false}
                className="font-mono text-xs"
                onChange={(event) => onChange(event.target.value)}
            />
        </div>
    );
}

function AgreementsTable({
    agreements,
    isLoading,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
}: {
    readonly agreements: Array<AgreementRead>;
    readonly isLoading: boolean;
    readonly searchQuery: string;
    readonly setSearchQuery: (value: string) => void;
    readonly statusFilter: AgreementStatus | 'all';
    readonly setStatusFilter: (value: AgreementStatus | 'all') => void;
}) {
    return (
        <Card className="min-w-0">
            <CardHeader>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <CardTitle className="text-base">Agreements</CardTitle>
                        <CardDescription>
                            PandaDoc document lifecycle tracked in Phoebe.
                        </CardDescription>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Input
                            value={searchQuery}
                            placeholder="Search agreements…"
                            className="w-full sm:w-[240px]"
                            onChange={(event) =>
                                setSearchQuery(event.target.value)
                            }
                        />
                        <Select
                            value={statusFilter}
                            onValueChange={(value) =>
                                setStatusFilter(
                                    value as AgreementStatus | 'all',
                                )
                            }
                        >
                            <SelectTrigger className="w-full sm:w-[170px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    All statuses
                                </SelectItem>
                                {Object.entries(STATUS_LABELS).map(
                                    ([value, label]) => (
                                        <SelectItem key={value} value={value}>
                                            {label}
                                        </SelectItem>
                                    ),
                                )}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="min-w-0">
                {isLoading ? (
                    <div className="text-muted-foreground flex h-32 items-center justify-center gap-2 text-sm">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading agreements…</span>
                    </div>
                ) : agreements.length === 0 ? (
                    <div className="text-muted-foreground flex h-32 items-center justify-center text-sm">
                        No agreements yet
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table className="min-w-[900px]">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Agreement</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Signer</TableHead>
                                    <TableHead>Template</TableHead>
                                    <TableHead>Sent</TableHead>
                                    <TableHead>Completed</TableHead>
                                    <TableHead>Document</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {agreements.map((agreement) => (
                                    <TableRow key={agreement.id}>
                                        <TableCell>
                                            <div className="font-medium">
                                                {agreement.title}
                                            </div>
                                            <div className="text-muted-foreground text-xs">
                                                {agreement.organization_name}
                                            </div>
                                            {agreement.attribution ? (
                                                <div className="text-muted-foreground text-xs">
                                                    Attribution:{' '}
                                                    {agreement.attribution}
                                                </div>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant={statusVariant(
                                                    agreement.status,
                                                )}
                                            >
                                                {
                                                    STATUS_LABELS[
                                                        agreement.status
                                                    ]
                                                }
                                            </Badge>
                                            {agreement.error_message ? (
                                                <div className="text-destructive mt-1 max-w-[180px] truncate text-xs">
                                                    {agreement.error_message}
                                                </div>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>
                                            <div>
                                                {agreement.signer_name ?? '—'}
                                            </div>
                                            <div className="text-muted-foreground text-xs">
                                                {agreement.signer_email ?? '—'}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="max-w-[220px] truncate">
                                                {agreement.pandadoc_template_name ??
                                                    agreement.pandadoc_template_id ??
                                                    '—'}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {formatDateTime(agreement.sent_at)}
                                        </TableCell>
                                        <TableCell>
                                            {formatDateTime(
                                                agreement.completed_at,
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {agreement.pandadoc_document_url ? (
                                                <Button
                                                    asChild={true}
                                                    size="sm"
                                                    variant="ghost"
                                                    className="gap-2"
                                                >
                                                    <a
                                                        href={
                                                            agreement.pandadoc_document_url
                                                        }
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        Open
                                                        <ExternalLink className="h-3.5 w-3.5" />
                                                    </a>
                                                </Button>
                                            ) : (
                                                <span className="text-muted-foreground text-sm">
                                                    —
                                                </span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
